import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageShell } from "@/components/site/PageShell";
import { Reveal } from "@/components/site/Reveal";
import { useSeo, absUrl } from "@/components/site/Seo";
import { ArticleBody, readingTime } from "@/components/site/ArticleBody";
import { Cover, fmtDate, ArticleCard } from "./Blog";

const SITE = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");

const ShareBar = ({ title, url }) => {
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success("Link artikel disalin")).catch(() => {});
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="blog-share">
      <span className="mr-1 text-sm text-zinc-400">Bagikan:</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`} target="_blank" rel="noopener noreferrer" data-testid="blog-share-whatsapp" className="rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-200 hover:border-volt">WhatsApp</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid="blog-share-facebook" className="rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-200 hover:border-volt">Facebook</a>
      <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid="blog-share-x" className="rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-200 hover:border-volt">X</a>
      <button onClick={copy} data-testid="blog-share-copy" className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-200 hover:border-volt"><Link2 className="h-4 w-4" /> Salin link</button>
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => {
    setPost(null);
    api.get(`/articles/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [slug]);
  const path = `/blog/${slug}`;
  const jsonLd = useMemo(() => (post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.seo_description || post.excerpt,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    mainEntityOfPage: `${SITE}${path}`,
    ...(post.cover_url ? { image: absUrl(post.cover_url) } : {}),
    ...(post.category ? { articleSection: post.category } : {}),
    author: { "@type": "Organization", name: "BagiPulsa", url: `${SITE}/` },
    publisher: { "@type": "Organization", name: "BagiPulsa", logo: { "@type": "ImageObject", url: `${SITE}/favicon-192.png` } },
  } : null), [post, path]);
  useSeo({
    title: post ? post.seo_title || post.title : "Blog BagiPulsa",
    description: post ? post.seo_description || post.excerpt : "",
    path,
    image: post?.cover_url,
    type: "article",
    jsonLd,
    noindex: post === false,
  });
  const related = Array.isArray(post?.related) ? post.related : [];
  return (
    <PageShell testId="blog-post-page">
      <article className="mx-auto max-w-3xl px-5 pb-10">
        <nav aria-label="breadcrumb" className="flex items-center gap-2 text-sm text-zinc-400">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-zinc-300 hover:text-volt"><ArrowLeft className="h-4 w-4" /> Semua artikel</Link>
          {post?.category ? <><span>/</span><Link to={`/blog?kategori=${encodeURIComponent(post.category)}`} data-testid="blog-post-category" className="text-volt hover:underline">{post.category}</Link></> : null}
        </nav>
        {post === false ? <p data-testid="blog-post-notfound" className="mt-10 text-zinc-400">Artikel tidak ditemukan.</p> : null}
        {post ? (
          <>
            <Reveal>
              <p className="mt-10 flex flex-wrap items-center gap-3 font-mono text-xs text-zinc-400"><span>{fmtDate(post.created_at)}</span><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {readingTime(post.content)} menit baca</span></p>
              <h1 data-testid="blog-post-title" className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">{post.title}</h1>
              {post.excerpt ? <p className="mt-6 text-lg text-zinc-300">{post.excerpt}</p> : null}
            </Reveal>
            <div className="mt-10 aspect-[16/9] overflow-hidden rounded-3xl"><Cover src={post.cover_url} title={post.title} alt={post.cover_alt} eager /></div>
            <div className="mt-12"><ArticleBody content={post.content} /></div>
            <div className="mt-12 border-t border-white/10 pt-8"><ShareBar title={post.title} url={`${SITE}${path}`} /></div>
          </>
        ) : null}
      </article>
      {related.length ? (
        <section className="mx-auto max-w-[1400px] px-5 pb-10 md:px-10" data-testid="blog-related">
          <h2 className="font-display text-2xl font-bold md:text-3xl">Artikel lainnya</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {related.map((a) => <ArticleCard key={a.id} a={a} testId={`blog-related-${a.slug}`} />)}
          </div>
        </section>
      ) : null}
    </PageShell>
  );
}
